import { useEffect, useRef } from 'react';
import { cn } from 'lib/utils';
import { getAgentAudioLevel } from '../services/voice-call-room';
import type { VoiceAgentState, VoiceCallStatus } from '../types/voice-call.types';

interface VoiceOrbProps {
  status: VoiceCallStatus;
  agentState: VoiceAgentState;
  className?: string;
}

const SPEAKING_SCALE_RANGE = 0.35;
const LEVEL_SMOOTHING = 0.25;

/** Claude-style orb: breathes while listening, pulses while thinking, swells with the agent's voice. */
export const VoiceOrb = ({ status, agentState, className }: VoiceOrbProps) => {
  const orbRef = useRef<HTMLDivElement>(null);
  const isInCall = status === 'in-call';
  const isSpeaking = isInCall && agentState === 'speaking';
  const isThinking = isInCall && agentState === 'thinking';
  const isConnecting = !isInCall || agentState === 'initializing';
  const visualState = isConnecting ? 'connecting' : agentState;

  // Drive the speaking animation from the live audio level without React re-renders.
  useEffect(() => {
    const orb = orbRef.current;
    if (!isSpeaking || !orb) return undefined;
    let frame = 0;
    let level = 0;
    const tick = () => {
      level += (getAgentAudioLevel() - level) * LEVEL_SMOOTHING;
      orb.style.transform = `scale(${1 + level * SPEAKING_SCALE_RANGE})`;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      orb.style.transform = '';
    };
  }, [isSpeaking]);

  return (
    <div
      data-testid="voice-orb"
      data-state={visualState}
      className={cn(
        'relative flex h-48 w-48 items-center justify-center sm:h-56 sm:w-56',
        className
      )}
    >
      <div
        aria-hidden
        className={cn(
          'voice-orb-halo absolute inset-[-25%] rounded-full',
          isThinking && 'voice-orb-halo-pulse',
          isSpeaking && 'opacity-90',
          !isThinking && !isSpeaking && 'opacity-50'
        )}
      />
      <div
        ref={orbRef}
        className={cn(
          'voice-orb h-36 w-36 rounded-full transition-opacity duration-300 sm:h-44 sm:w-44',
          isConnecting && 'animate-pulse opacity-60',
          !isConnecting && !isSpeaking && !isThinking && 'voice-orb-breathe',
          isThinking && 'opacity-90'
        )}
      />
      {isThinking && (
        <div
          aria-hidden
          className="absolute -bottom-8 flex items-center gap-1.5"
          data-testid="voice-orb-thinking-dots"
        >
          {[0, 1, 2].map((dot) => (
            <span key={dot} className="loading-dot h-2 w-2 rounded-full bg-[var(--voice-orb-2)]" />
          ))}
        </div>
      )}
    </div>
  );
};
